import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import { Tag } from "components/DesignSystem/Tag/Tag";
import Loader from "components/DesignSystem/Loader/loader";
import {
  useGetAIReconcileDetailQuery,
  useGetReconciliationHistoryQuery,
} from "store/apis/reconciliation";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { LAST_RECONCILATION_STATUS } from "constants/dataSource";
import { useHistory } from "react-router-dom";
import { FilingDueIcon } from "components/icons/FilingDueIcon";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";

const PENDING = "pending";
const COMPLETED = "completed";
const EXPIRED = "expired";

export const AIReconcileCard = ({
  entityId,
  reconciliationStateId,
  size,
}: {
  entityId: string;
  reconciliationStateId: string;
  size?: "regular" | "block";
}) => {
  const { data, isLoading, isFetching } = useGetAIReconcileDetailQuery(
    { entityId, reconciliationStateId },
    { skip: !entityId || !reconciliationStateId }
  );
  const { link } = useConstructInternalLink();
  const history = useHistory();

  const bankAccountId = data?.entity_bank_account_id;

  const handleReviewClick = () => {
    history.push(
      link(`/books/ai-reconciliation/${reconciliationStateId}`, {
        moreQuery: { entity: entityId },
      })
    );
  };

  const handleViewReconciliation = () => {
    if (!bankAccountId) return;
    history.push(
      link(`/books/reconciliation/history/${bankAccountId}`, {
        moreQuery: { entity: entityId },
      })
    );
  };

  if (isLoading || isFetching) {
    return (
      <div className="t-min-w-72 sm:t-min-w-0">
        <ChatCard.Root size={size} className="t-w-full">
          <div className="t-flex t-items-center t-justify-center t-h-64">
            <Loader size="small" />
          </div>
        </ChatCard.Root>
      </div>
    );
  }

  const isCompleted =
    data?.last_reconcilation_status === LAST_RECONCILATION_STATUS.COMPLETED;

  const isExpired =
    data?.last_reconcilation_status === LAST_RECONCILATION_STATUS.EXPIRED;

  const dateRange = data
    ? `${dayjs(data.reconciliation_start_date).format(DISPLAY_DATE)} - ${dayjs(
        data.reconciliation_end_date
      ).format(DISPLAY_DATE)}`
    : "";

  const STATUS_CONFIG = {
    expired: { tagType: "gray" as const, label: "Expired" },
    completed: { tagType: "green" as const, label: "Completed" },
    pending: { tagType: "purple" as const, label: "Review pending" },
  };

  let cardStatus: keyof typeof STATUS_CONFIG = PENDING;
  if (isExpired) cardStatus = EXPIRED;
  if (isCompleted) cardStatus = COMPLETED;

  const { tagType, label } = STATUS_CONFIG[cardStatus];

  return (
    <div className="t-min-w-72 sm:t-min-w-0">
      <ChatCard.Root size={size} className="t-w-full">
        <ChatCard.Header>
          <div className="t-flex t-items-center t-gap-1.5">
            <FilingDueIcon />
            Reconciliation
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-flex t-flex-col t-gap-1">
            <div className="t-font-medium t-text-body-sm">
              {data?.account_name}
            </div>
            <div className="t-text-body-sm t-text-text-30">{dateRange}</div>
          </div>
          <div>
            <Tag tagType={tagType} rounded icon={false}>
              {label}
            </Tag>
          </div>
          {cardStatus === PENDING && (
            <Button
              size="small"
              customType="primary"
              block
              onClick={handleReviewClick}
            >
              Review & Approve
            </Button>
          )}
          {cardStatus === COMPLETED && (
            <Button size="small" block onClick={handleViewReconciliation}>
              View reconciliation
            </Button>
          )}
        </ChatCard.Body>
      </ChatCard.Root>
    </div>
  );
};
